import { useState, useEffect, useRef, useCallback } from "react";
import { documents } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Download, FileText, Shield, CreditCard, Award, FolderArchive, AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const categoryIcons = {
  GST: Shield,
  PAN: CreditCard,
  License: Award,
  Certificate: Award,
  Other: FileText,
};

const categoryColors = {
  GST: "#FF5C1A",
  PAN: "#FFB800",
  License: "#2ECC71",
  Certificate: "#E74C3C",
  Other: "#888880",
};

const docCategories = ["GST", "PAN", "License", "Certificate", "Other"];

export default function DocumentVault() {
  const [docList, setDocList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [filterCategory, setFilterCategory] = useState("");
  const [form, setForm] = useState({ name: "", category: "GST", expiry_date: "" });
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const fetchDocs = useCallback(async () => {
    try {
      const params = {};
      if (filterCategory) params.category = filterCategory;
      const r = await documents.list(params);
      setDocList(r.data);
    } catch (err) {
      toast.error("Failed to load documents");
    } finally {
      setLoading(false);
    }
  }, [filterCategory]);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error("File too large. Max 10MB.");
        return;
      }
      setSelectedFile(file);
      if (!form.name) {
        setForm({ ...form, name: file.name });
      }
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!selectedFile) {
      toast.error("Please select a file");
      return;
    }
    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = reader.result.split(",")[1];
        await documents.create({
          name: form.name,
          category: form.category,
          expiry_date: form.expiry_date,
          file_data: base64,
          file_type: selectedFile.type,
          file_size: selectedFile.size,
        });
        toast.success("Document uploaded");
        setDialogOpen(false);
        setForm({ name: "", category: "GST", expiry_date: "" });
        setSelectedFile(null);
        fetchDocs();
      };
      reader.readAsDataURL(selectedFile);
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (docId, docName) => {
    try {
      const r = await documents.download(docId);
      const byteCharacters = atob(r.data.file_data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: r.data.file_type || "application/octet-stream" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = docName;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Download failed");
    }
  };

  const handleDelete = async (id) => {
    try {
      await documents.remove(id);
      toast.success("Document deleted");
      fetchDocs();
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  const formatSize = (bytes) => {
    if (bytes > 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
    if (bytes > 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${bytes} B`;
  };

  const isExpiringSoon = (date) => {
    if (!date) return false;
    const expiry = new Date(date);
    const now = new Date();
    const diff = (expiry - now) / (1000 * 60 * 60 * 24);
    return diff >= 0 && diff <= 30;
  };

  const isExpired = (date) => {
    if (!date) return false;
    return new Date(date) < new Date();
  };

  const inputCls = "w-full bg-[#0A0A08] border border-[#27272A] text-[#F5F2E8] px-3 py-2.5 text-sm focus:border-[#FF5C1A] focus:ring-1 focus:ring-[#FF5C1A] outline-none transition-colors placeholder:text-[#444]";

  return (
    <div className="space-y-6" data-testid="document-vault">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="font-heading text-3xl tracking-[2px]">DOCUMENT VAULT</h2>
          <p className="text-sm text-[#888880] mt-1">{docList.length} documents stored</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <button data-testid="upload-doc-btn" className="bg-[#FF5C1A] text-[#0A0A08] px-5 py-2.5 font-mono text-xs font-bold tracking-widest uppercase hover:bg-[#FFB800] transition-colors flex items-center gap-2 shadow-[4px_4px_0px_0px_rgba(0,0,0,0.5)] active:translate-y-[2px] active:shadow-none">
              <Plus size={16} /> Upload
            </button>
          </DialogTrigger>
          <DialogContent className="bg-[#111110] border-[#27272A] text-[#F5F2E8] max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl tracking-[2px]">UPLOAD DOCUMENT</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUpload} className="space-y-4 mt-4">
              <div>
                <label className="block font-mono text-[10px] text-[#888880] tracking-[2px] uppercase mb-1">File</label>
                <input
                  data-testid="doc-file-input"
                  ref={fileInputRef}
                  type="file"
                  onChange={handleFileChange}
                  className="w-full text-sm text-[#888880] file:mr-3 file:py-2 file:px-4 file:bg-[#FF5C1A]/10 file:text-[#FF5C1A] file:border file:border-[#FF5C1A]/20 file:font-mono file:text-xs file:tracking-widest file:uppercase file:cursor-pointer"
                />
                {selectedFile && <p className="text-xs text-[#888880] mt-1 font-mono">{formatSize(selectedFile.size)}</p>}
              </div>
              <div>
                <label className="block font-mono text-[10px] text-[#888880] tracking-[2px] uppercase mb-1">Document Name</label>
                <input data-testid="doc-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className={inputCls} placeholder="GST Registration Certificate" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-mono text-[10px] text-[#888880] tracking-[2px] uppercase mb-1">Category</label>
                  <select data-testid="doc-category-select" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputCls}>
                    {docCategories.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block font-mono text-[10px] text-[#888880] tracking-[2px] uppercase mb-1">Expiry Date</label>
                  <input data-testid="doc-expiry-input" type="date" value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} className={inputCls} />
                </div>
              </div>
              <button data-testid="submit-doc-btn" type="submit" disabled={uploading} className="w-full bg-[#FF5C1A] text-[#0A0A08] py-3 font-mono text-xs font-bold tracking-widest uppercase hover:bg-[#FFB800] transition-colors disabled:opacity-50">
                {uploading ? "Uploading..." : "Upload Document"}
              </button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Category Filter */}
      <div className="flex gap-2 flex-wrap">
        <button
          data-testid="filter-all-docs"
          onClick={() => setFilterCategory("")}
          className={`font-mono text-[10px] px-3 py-1.5 tracking-widest uppercase transition-colors ${!filterCategory ? 'bg-[#FF5C1A] text-[#0A0A08]' : 'bg-[#111110] border border-[#27272A] text-[#888880] hover:text-[#F5F2E8]'}`}
        >
          All
        </button>
        {docCategories.map((c) => (
          <button
            key={c}
            data-testid={`filter-${c.toLowerCase()}-docs`}
            onClick={() => setFilterCategory(c)}
            className={`font-mono text-[10px] px-3 py-1.5 tracking-widest uppercase transition-colors ${filterCategory === c ? 'bg-[#FF5C1A] text-[#0A0A08]' : 'bg-[#111110] border border-[#27272A] text-[#888880] hover:text-[#F5F2E8]'}`}
          >
            {c}
          </button>
        ))}
      </div>

      {/* Document Grid */}
      {loading ? (
        <div className="text-center py-12 text-[#888880] font-mono text-sm">Loading...</div>
      ) : docList.length === 0 ? (
        <div className="text-center py-16 bg-[#111110] border border-[#27272A]">
          <FolderArchive size={48} className="mx-auto text-[#27272A] mb-4" />
          <div className="text-[#888880] font-mono text-sm">No documents. Upload your first file.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {docList.map((d) => {
            const Icon = categoryIcons[d.category] || FileText;
            const color = categoryColors[d.category] || "#888880";
            const expired = isExpired(d.expiry_date);
            const expiring = isExpiringSoon(d.expiry_date);

            return (
              <div key={d.id} data-testid={`doc-item-${d.id}`} className="bg-[#111110] border border-[#27272A] p-5 group hover:border-[#FF5C1A]/30 transition-colors relative">
                {(expired || expiring) && (
                  <div className={`absolute top-3 right-3 flex items-center gap-1 font-mono text-[10px] ${expired ? 'text-[#E74C3C]' : 'text-[#FFB800]'}`}>
                    <AlertTriangle size={12} />
                    {expired ? "EXPIRED" : "EXPIRING"}
                  </div>
                )}
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 rounded-sm" style={{ backgroundColor: `${color}15` }}>
                    <Icon size={20} style={{ color }} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{d.name}</div>
                    <div className="font-mono text-[10px] text-[#888880] uppercase">{d.category}</div>
                  </div>
                </div>
                <div className="flex items-center justify-between text-xs text-[#888880]">
                  <div className="font-mono">
                    {d.expiry_date ? `Exp: ${d.expiry_date}` : "No expiry"}
                  </div>
                  {d.file_size > 0 && <span className="font-mono">{formatSize(d.file_size)}</span>}
                </div>
                <div className="flex gap-2 mt-4 pt-4 border-t border-[#27272A]">
                  <button data-testid={`download-doc-${d.id}`} onClick={() => handleDownload(d.id, d.name)} className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-[#FF5C1A]/10 text-[#FF5C1A] font-mono text-[10px] tracking-widest uppercase hover:bg-[#FF5C1A]/20 transition-colors">
                    <Download size={12} /> Download
                  </button>
                  <button data-testid={`delete-doc-${d.id}`} onClick={() => handleDelete(d.id)} className="py-2 px-3 text-[#888880] hover:text-[#E74C3C] hover:bg-[#E74C3C]/10 transition-colors">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
